import { useEffect, useState } from "react";
import { fetchAuthedObjectUrl } from "../utils/authedResource";

/**
 * Resolves a protected screenshot/recording URL into a blob URL that `<img>` or
 * `<video>` can render.
 *
 * These files authenticate with the `Authorization` header, which an element
 * `src` cannot send, so the bytes are fetched with the credential attached and
 * exposed as an object URL. The URL is revoked when it is replaced or the
 * component unmounts.
 *
 * @param {string|null|undefined} url
 * @param {object} [options]
 * @param {string|null} [options.fallback]
 * @returns {{ src: string|null, isLoading: boolean, error: Error|null }}
 */
export const useAuthedMediaSrc = (url, { fallback = null } = {}) => {
  const [state, setState] = useState({ src: fallback, isLoading: Boolean(url), error: null });

  useEffect(() => {
    if (!url) {
      setState({ src: fallback, isLoading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;
    setState({ src: fallback, isLoading: true, error: null });

    fetchAuthedObjectUrl(url)
      .then((resolved) => {
        if (cancelled) {
          URL.revokeObjectURL(resolved);
          return;
        }
        objectUrl = resolved;
        setState({ src: resolved, isLoading: false, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ src: fallback, isLoading: false, error });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, fallback]);

  return state;
};

export default useAuthedMediaSrc;
